import axios from "axios";
import React, { createContext } from "react";

import {
  CreateRoleDefinitionPayload,
  CreateRoleDefinitionResponse,
  GetCompanyRolesResponse,
  GetManagedUsersResponse,
  GetPermissionsResponse,
  GetRoleDefinitionResponse,
  GetSelfResponse,
  GetTeamsResponse,
  GetUserDetailsResponse,
  GetUserProductsResponse,
  GetUsersPayload,
  GetUsersQueryParams,
  UpdateRoleDefinitionPayload,
  UpdateRoleDefinitionResponse,
  UpdateUserProductsResponse,
  UpdateUserRolesPayload,
  UpdateUserRolesResponse,
} from "@/lib/dto";
import {
  ApiError,
  DomainRemovalNeedsConfirmationError,
  UserCreationConflictError,
  apiErrorWithMessage,
  toApiError,
} from "@/lib/error";
import {
  AccessRequestDto,
  AccessRequestStatus,
  AccessRequestValidationResult,
  AccountStateCountsDto,
  AdGroupDto,
  AdGroupProductMappingDto,
  AdGroupRoleMappingDto,
  AdGroupSyncPreviewDto,
  AdGroupSyncPreviewRequestDto,
  AdGroupSyncResultDto,
  AdGroupSyncStatusDto,
  AdminResetPasswordResponse,
  ApproverDto,
  AuthMethod,
  AuthTokensSettingsDto,
  ClientCompanyDto,
  CompanyApiKeyCreatedDto,
  CompanyApiKeyDto,
  CompanyInfoDto,
  CompanyPluginDto,
  CompanyResource,
  CompanySamlConfigurationDto,
  CompanySearchResultDto,
  ConnectMicrosoftCompanyAppDto,
  CreateAccessRequestCommand,
  CreateAdGroupProductMappingDto,
  CreateAdGroupRoleMappingDto,
  CursorPageResponse,
  DashboardDto,
  DefaultProductsSettingsDto,
  DefaultRolesSettingsDto,
  DynamicRoleAssignmentDto,
  DynamicRoleAssignmentPreviewDto,
  DynamicRoleAssignmentRequestDto,
  EntraIdConfigDto,
  ExportFormat,
  JiraIssueDto,
  JobTitleApplyRequestDto,
  JobTitleApplyResultDto,
  JobTitleReconciliationDto,
  JobTitleResyncResultDto,
  MicrosoftCompanyAppConnectResultDto,
  MicrosoftIntegrationStatusDto,
  MoveTeamRequest,
  Page,
  PageResponse,
  RoleMatchedUsersDto,
  RoleTemplateDto,
  ScimConfigDto,
  ScimTokenGeneratedDto,
  SecurityLogDto,
  SecurityLogStatus,
  SecurityLogView,
  SecurityOperation,
  ServiceAccountStatusDto,
  SignInDiagnosisDto,
  SkillPanelCustomFieldsDto,
  SkillPanelDashboardDto,
  SkillPanelVisibilityDto,
  StructureApplyResultDto,
  StructureBatchStatusDto,
  StructureImportModeDto,
  StructurePlanDto,
  SupportPersonaSessionDto,
  TeamAssignmentSettingsDto,
  TeamDto,
  TeamImportListDto,
  TeamMemberListDto,
  TeamTreeBrowserDetailsDto,
  TeamTreeBrowserNodeDto,
  UpdateAdGroupProductMappingDto,
  UpdateAdGroupRoleMappingDto,
  UpdateCompanyDomainsDto,
  UpdateDefaultRolesSettingsDto,
  UpdateEntraIdConfigDto,
  UpdateSamlConfigurationDto,
  UpdateScimConfigDto,
  UserChangeDto,
  UserCreationContextDto,
  UserCreationDto,
  UserCreationRequest,
  UserCreationScheduledDto,
  UserImportListDto,
  UserMapSyncDto,
  UserProducts,
} from "@/types/auth";

// Re-exported from the generated types so the wire format stays in sync with the Java enum.
export type { ExportFormat };

export type RestApiContextValue = ReturnType<typeof createApiProvider>;

export const RestApiContext = createContext<RestApiContextValue | undefined>(undefined);

/**
 * `StructureFileException` carries a `409` (structure changed since preview) and, for `400`s,
 * only a `logref` and `message` (the first issue's message) — the global handler's catch-all
 * never serialises the full `issues` array. The `issues` branch is kept for a future backend
 * that does expose them; today every 400 falls through to `message`.
 */
const toStructureImportError = (e: unknown, fallback: string): ApiError => {
  if (axios.isAxiosError(e)) {
    if (e.response?.status === 409) {
      return apiErrorWithMessage(
        e,
        "The company structure changed since the preview. Preview the file again to see what applying it would change now.",
      );
    }
    const data = e.response?.data as
      | {
          issues?: { sheet: string; row?: number; column?: string; message: string }[];
          message?: string;
        }
      | undefined;
    const issues = data?.issues;
    if (issues?.length) {
      return apiErrorWithMessage(
        e,
        issues
          .map(
            (i) =>
              `${i.sheet}${i.row ? ` row ${i.row}` : ""}${i.column ? ` (${i.column})` : ""}: ${i.message}`,
          )
          .join("\n"),
      );
    }
    if (data?.message) {
      return apiErrorWithMessage(e, data.message);
    }
  }
  return apiErrorWithMessage(e, fallback);
};

const createApiProvider = () => {
  const getSelf = async () => {
    const url = "/api/self";
    try {
      const { data } = await axios.get<GetSelfResponse>(url);

      if (!data || typeof data !== "object") {
        // When cookies expire (or become invalid) the endpoint may respond with
        // an HTTP 302 redirect, which effectively makes axios return an HTML
        // response, which is invalid here.
        throw new Error("Invalid response from /self endpoint");
      }

      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, `Error while fetching logged in user details.`);
    }
  };

  const getCompany = async (companyId: string) => {
    const url = `/api/companies/${companyId}`;
    try {
      const { data } = await axios.get<CompanyResource>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, `Failed to fetch company with id ${companyId}.`);
    }
  };

  const getClientCompanies = async () => {
    const url = `/api/companies/clients`;
    try {
      const { data } = await axios.get<ClientCompanyDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, `Failed to fetch client companies.`);
    }
  };

  const searchCompanies = async (q: string) => {
    const { data } = await axios.get<CompanySearchResultDto[]>(`/api/company-search`, {
      params: { q },
    });
    return data;
  };

  const getApprovers = async (companyId: string) => {
    const url = `/api/access-requests/approvers`;
    try {
      const { data } = await axios.get<ApproverDto[]>(url, { params: { companyId } });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching approvers");
    }
  };

  const validateTargetUser = async (companyId: string, email: string) => {
    const url = `/api/access-requests/validate-target`;
    try {
      const { data } = await axios.get<ApproverDto>(url, { params: { companyId, email } });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "User not found with this email in the selected company");
    }
  };

  const getAuthTokensSettings = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/auth-tokens`;
    try {
      const { data } = await axios.get<AuthTokensSettingsDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(
        e,
        `Failed to fetch auth tokens settings for company ${companyId}.`,
      );
    }
  };

  const updateAuthTokensSettings = async (companyId: string, dto: AuthTokensSettingsDto) => {
    const url = `/api/companies/${companyId}/settings/auth-tokens`;
    try {
      const { data } = await axios.put<AuthTokensSettingsDto>(url, dto, {
        headers: { "Content-Type": "application/json" },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(
        e,
        `Failed to update auth tokens settings for company ${companyId}.`,
      );
    }
  };

  const getTeamAssignmentSettings = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/team-assignment`;
    try {
      const { data } = await axios.get<TeamAssignmentSettingsDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(
        e,
        `Failed to fetch team assignment settings for company ${companyId}.`,
      );
    }
  };

  const updateTeamAssignmentSettings = async (
    companyId: string,
    dto: TeamAssignmentSettingsDto,
  ) => {
    const url = `/api/companies/${companyId}/settings/team-assignment`;
    try {
      const { data } = await axios.put<TeamAssignmentSettingsDto>(url, dto, {
        headers: { "Content-Type": "application/json" },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(
        e,
        `Failed to update team assignment settings for company ${companyId}.`,
      );
    }
  };

  const getCompanyInfo = async (companyId: string) => {
    const url = `/api/companies/${companyId}/info`;
    try {
      const { data } = await axios.get<CompanyInfoDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, `Failed to fetch company info for company ${companyId}.`);
    }
  };

  const getDefaultRolesSettings = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/default-roles`;
    try {
      const { data } = await axios.get<DefaultRolesSettingsDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(
        e,
        `Failed to fetch default roles settings for company ${companyId}.`,
      );
    }
  };

  const updateDefaultRolesSettings = async (
    companyId: string,
    dto: UpdateDefaultRolesSettingsDto,
  ) => {
    const url = `/api/companies/${companyId}/settings/default-roles`;
    try {
      const { data } = await axios.put<DefaultRolesSettingsDto>(url, dto, {
        headers: { "Content-Type": "application/json" },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(
        e,
        `Failed to update default roles settings for company ${companyId}.`,
      );
    }
  };

  const getDefaultProducts = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/default-products`;
    try {
      const { data } = await axios.get<DefaultProductsSettingsDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, `Failed to fetch default products for company ${companyId}.`);
    }
  };

  const updateDefaultProducts = async (companyId: string, dto: DefaultProductsSettingsDto) => {
    const url = `/api/companies/${companyId}/settings/default-products`;
    try {
      const { data } = await axios.put<DefaultProductsSettingsDto>(url, dto);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(
            e,
            `Only a superadmin can change the products new users are granted.`,
          );
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to update default products for company ${companyId}.`);
    }
  };

  const getSamlConfiguration = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/saml-configuration`;
    try {
      const { data } = await axios.get<CompanySamlConfigurationDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to view SAML configuration.`);
        }
        if (e.response?.status === 404) {
          throw apiErrorWithMessage(e, `Company ${companyId} not found.`);
        }
      }
      throw apiErrorWithMessage(e, `Failed to fetch SAML configuration for company ${companyId}.`);
    }
  };

  const setDefaultSamlConfiguration = async (companyId: string, configId: number) => {
    const url = `/api/companies/${companyId}/settings/saml-configuration/default/${configId}`;
    try {
      await axios.put(url);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to modify SAML configuration.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to set default SAML configuration.`);
    }
  };

  const updateSamlConfiguration = async (
    companyId: string,
    configId: number,
    update: UpdateSamlConfigurationDto,
  ) => {
    const url = `/api/companies/${companyId}/settings/saml-configuration/${configId}`;
    try {
      await axios.put(url, update);
    } catch (e: unknown) {
      throw toSamlSettingsError(e, "the IdP connection");
    }
  };

  const updateCompanyDomains = async (companyId: string, update: UpdateCompanyDomainsDto) => {
    const url = `/api/companies/${companyId}/settings/company-domains`;
    try {
      await axios.put(url, update);
    } catch (e: unknown) {
      if (
        axios.isAxiosError(e) &&
        e.response?.data?.errorType === "DOMAIN_REMOVAL_NEEDS_CONFIRMATION"
      ) {
        throw new DomainRemovalNeedsConfirmationError(
          e.response.data.message,
          e.response.data.domains ?? [],
        );
      }
      throw toSamlSettingsError(e, "company domains");
    }
  };

  // A 403 body says only "Token validation failed", so it is replaced with a message the form can show.
  const toSamlSettingsError = (e: unknown, subject: string) => {
    const error = toApiError(e, `Failed to update ${subject}.`);
    return error.status === 403
      ? apiErrorWithMessage(e, `You don't have permission to modify ${subject}.`)
      : error;
  };

  const clearDefaultSamlConfiguration = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/saml-configuration/default`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to modify SAML configuration.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to clear default SAML configuration.`);
    }
  };

  const getEntraIdConfiguration = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/entraid-configuration`;
    try {
      const { data } = await axios.get<EntraIdConfigDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to view Entra ID configuration.`);
        }
        if (e.response?.status === 404) {
          throw apiErrorWithMessage(e, `Company ${companyId} not found.`);
        }
      }
      throw apiErrorWithMessage(
        e,
        `Failed to fetch Entra ID configuration for company ${companyId}.`,
      );
    }
  };

  const updateEntraIdConfiguration = async (companyId: string, dto: UpdateEntraIdConfigDto) => {
    const url = `/api/companies/${companyId}/settings/entraid-configuration`;
    try {
      const { data } = await axios.put<EntraIdConfigDto>(url, dto, {
        headers: { "Content-Type": "application/json" },
      });
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(
            e,
            `You don't have permission to modify Entra ID configuration.`,
          );
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(
        e,
        `Failed to update Entra ID configuration for company ${companyId}.`,
      );
    }
  };

  const getMicrosoftIntegrationStatus = async (companyId: string) => {
    const url = `/api/companies/${companyId}/integrations/microsoft`;
    try {
      const { data } = await axios.get<MicrosoftIntegrationStatusDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(
            e,
            `You don't have permission to view Microsoft Graph integration.`,
          );
        }
        if (e.response?.status === 404) {
          throw apiErrorWithMessage(e, `Company ${companyId} not found.`);
        }
      }
      throw apiErrorWithMessage(
        e,
        `Failed to fetch Microsoft Graph integration for company ${companyId}.`,
      );
    }
  };

  const disconnectMicrosoftIntegration = async (companyId: string) => {
    const url = `/api/companies/${companyId}/integrations/microsoft`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(
            e,
            `You don't have permission to modify Microsoft Graph integration.`,
          );
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(
        e,
        `Failed to disconnect Microsoft Graph integration for company ${companyId}.`,
      );
    }
  };

  const disconnectMicrosoftBot = async (companyId: string) => {
    const url = `/api/companies/${companyId}/integrations/microsoft/bot`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(
            e,
            `You don't have permission to modify Microsoft Graph integration.`,
          );
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to disconnect the Teams bot for company ${companyId}.`);
    }
  };

  const connectMicrosoftCompanyApp = async (
    companyId: string,
    body: ConnectMicrosoftCompanyAppDto,
  ) => {
    const url = `/api/companies/${companyId}/integrations/microsoft/app`;
    try {
      const { data } = await axios.post<MicrosoftCompanyAppConnectResultDto>(url, body);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(
            e,
            `You don't have permission to modify Microsoft Graph integration.`,
          );
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, "Failed to connect the application.");
    }
  };

  const getScimConfiguration = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/scim-configuration`;
    try {
      const { data } = await axios.get<ScimConfigDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to view SCIM configuration.`);
        }
        if (e.response?.status === 404) {
          throw apiErrorWithMessage(e, `Company ${companyId} not found.`);
        }
      }
      throw apiErrorWithMessage(e, `Failed to fetch SCIM configuration for company ${companyId}.`);
    }
  };

  const generateScimToken = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/scim-configuration/token`;
    try {
      const { data } = await axios.post<ScimTokenGeneratedDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to generate SCIM token.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to generate SCIM token for company ${companyId}.`);
    }
  };

  const revokeScimToken = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/scim-configuration/token`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to revoke SCIM token.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to revoke SCIM token for company ${companyId}.`);
    }
  };

  const updateScimConfiguration = async (companyId: string, dto: UpdateScimConfigDto) => {
    const url = `/api/companies/${companyId}/settings/scim-configuration`;
    try {
      const { data } = await axios.put<ScimConfigDto>(url, dto);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to update SCIM configuration.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to update SCIM configuration for company ${companyId}.`);
    }
  };

  const listCompanyApiKeys = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/api-keys`;
    try {
      const { data } = await axios.get<CompanyApiKeyDto[]>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to view API keys.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to load API keys for company ${companyId}.`);
    }
  };

  const createCompanyApiKey = async (companyId: string, name: string) => {
    const url = `/api/companies/${companyId}/settings/api-keys`;
    try {
      const { data } = await axios.post<CompanyApiKeyCreatedDto>(url, { name });
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to create API keys.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to create API key for company ${companyId}.`);
    }
  };

  const revokeCompanyApiKey = async (companyId: string, prefix: string) => {
    const url = `/api/companies/${companyId}/settings/api-keys/${encodeURIComponent(prefix)}`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to revoke API keys.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to revoke API key for company ${companyId}.`);
    }
  };

  const getServiceAccountStatus = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/service-account`;
    try {
      const { data } = await axios.get<ServiceAccountStatusDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to view the service account.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(
        e,
        `Failed to load service account status for company ${companyId}.`,
      );
    }
  };

  const createServiceAccount = async (companyId: string) => {
    const url = `/api/companies/${companyId}/settings/service-account`;
    try {
      const { data } = await axios.post<ServiceAccountStatusDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, `You don't have permission to create a service account.`);
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to create service account for company ${companyId}.`);
    }
  };

  const getCompanyPlugins = async (companyId: string) => {
    const url = `/api/companies/${companyId}/plugins`;
    try {
      const { data } = await axios.get<CompanyPluginDto[]>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(e, "You don't have permission to view plugins.");
        }
        if (e.response?.status === 502) {
          throw apiErrorWithMessage(e, "SkillPanel unavailable.");
        }
      }
      throw apiErrorWithMessage(e, `Failed to fetch plugins for company ${companyId}.`);
    }
  };

  const enableCompanyPlugin = async (companyId: string, pluginId: string) => {
    const url = `/api/companies/${companyId}/plugins/${encodeURIComponent(pluginId)}`;
    try {
      const { data } = await axios.put<CompanyPluginDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 502) {
          throw apiErrorWithMessage(e, "SkillPanel unavailable.");
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to enable plugin ${pluginId}.`);
    }
  };

  const disableCompanyPlugin = async (companyId: string, pluginId: string) => {
    const url = `/api/companies/${companyId}/plugins/${encodeURIComponent(pluginId)}`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 502) {
          throw apiErrorWithMessage(e, "SkillPanel unavailable.");
        }
        if (e.response?.data?.message) {
          throw apiErrorWithMessage(e, e.response.data.message);
        }
      }
      throw apiErrorWithMessage(e, `Failed to disable plugin ${pluginId}.`);
    }
  };

  const getTeams = async (companyId: string) => {
    const url = `/api/companies/${companyId}/teams`;
    try {
      const { data } = await axios.get<GetTeamsResponse>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching Teams");
    }
  };

  const updateTeam = async (teamId: string, dto: TeamDto, companyId: string) => {
    const { data } = await axios.put<TeamDto>(`/api/teams/${teamId}?company=${companyId}`, dto);
    return data;
  };

  const moveTeam = async (teamId: string, parentTeamId: string, companyId: string) => {
    const request: MoveTeamRequest = { parentTeamId };
    const { data } = await axios.put<TeamDto>(
      `/api/teams/${teamId}/parent?company=${companyId}`,
      request,
    );
    return data;
  };

  const getUsers = async (
    companyId: string,
    query: GetUsersPayload,
    queryParams?: GetUsersQueryParams,
  ) => {
    const url = `/api/companies/${companyId}/users`;
    try {
      const { data } = await axios.post<GetManagedUsersResponse>(
        url,
        {
          email: query.email,
          teamIds: query.teamIds,
          roleIds: query.roleIds,
          enabled: query.enabled,
          accountState: query.accountState,
          cannotSignIn: query.cannotSignIn,
          dormantPermissions: query.dormantPermissions,
          dormantProduct: query.dormantProduct,
        },
        {
          params: queryParams,
          headers: { "Content-Type": "application/json" },
        },
      );
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching ManagedUsers");
    }
  };

  // The counts behind the list's view tabs, under the list's search, team, role and
  // dormant-permissions filters
  const getUserCounts = async (
    companyId: string,
    query: Pick<
      GetUsersPayload,
      "email" | "teamIds" | "roleIds" | "dormantPermissions" | "dormantProduct"
    >,
  ) => {
    const { data } = await axios.post<AccountStateCountsDto>(
      `/api/companies/${companyId}/users/counts`,
      {
        email: query.email,
        teamIds: query.teamIds,
        roleIds: query.roleIds,
        dormantPermissions: query.dormantPermissions,
        dormantProduct: query.dormantProduct,
      },
    );
    return data;
  };

  const getUserDetails = async (id: string, companyId: string) => {
    const url = `/api/companies/${companyId}/users/${id}`;
    try {
      const { data } = await axios.get<GetUserDetailsResponse>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, `Error while fetching details for user ${id}`);
    }
  };

  const getSignInDiagnosis = async (companyId: string, userId: string) => {
    const url = `/api/companies/${companyId}/users/${userId}/sign-in-diagnosis`;
    try {
      const { data } = await axios.get<SignInDiagnosisDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while checking whether the user can sign in");
    }
  };

  const getUserProducts = async (companyId: string, userId: string) => {
    const url = `/api/companies/${companyId}/users/${userId}/products`;
    try {
      const { data } = await axios.get<GetUserProductsResponse>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching User Products");
    }
  };

  const updateUserProducts = async (companyId: string, userId: string, products: UserProducts) => {
    const url = `/api/companies/${companyId}/users/${userId}/products`;
    try {
      const { data } = await axios.put<UpdateUserProductsResponse>(url, {
        products: products.products,
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while updating User Products.");
    }
  };

  const updateUserRoles = async (
    companyId: string,
    userId: string,
    userRoleMappingUpdate: UpdateUserRolesPayload,
  ) => {
    const url = `/api/companies/${companyId}/users/${userId}/roleDefinitions`;
    try {
      const { data } = await axios.put<UpdateUserRolesResponse>(url, userRoleMappingUpdate, {
        headers: { "Content-Type": "application/json" },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while updating Roles");
    }
  };

  const updateUserAuthMethod = async (
    companyId: string,
    userId: string,
    authMethod: AuthMethod,
  ) => {
    const url = `/api/users/${userId}/auth-method?company=${companyId}`;
    try {
      await axios.put(
        url,
        { authMethod },
        {
          headers: { "Content-Type": "application/json" },
        },
      );
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while updating auth method");
    }
  };

  const updateUserEnabled = async (companyId: string, userId: string, enabled: boolean) => {
    const url = `/api/users/${userId}/enabled?company=${companyId}`;
    try {
      await axios.put(
        url,
        { enabled },
        {
          headers: { "Content-Type": "application/json" },
        },
      );
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while updating account status");
    }
  };

  const updateUserHiddenInSkillPanel = async (
    companyId: string,
    userId: string,
    hidden: boolean,
  ) => {
    const url = `/api/users/${userId}/hidden-in-skillpanel?company=${companyId}`;
    try {
      await axios.put(
        url,
        { hidden },
        {
          headers: { "Content-Type": "application/json" },
        },
      );
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while updating SkillPanel visibility");
    }
  };

  const updateUserCustomFields = async (
    companyId: string,
    userId: string,
    values: Record<string, string>,
  ) => {
    const url = `/api/companies/${companyId}/users/${userId}/custom-fields`;
    try {
      await axios.put(url, values, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while updating additional information");
    }
  };

  const unlockUserAccount = async (companyId: string, userId: string) => {
    const url = `/api/users/${userId}/unlock?company=${companyId}`;
    try {
      await axios.put(url);
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while unlocking account");
    }
  };

  const adminResetPassword = async (userId: string) => {
    const url = `/api/users/${userId}/reset-password`;
    try {
      const { data } = await axios.post<AdminResetPasswordResponse>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while resetting password");
    }
  };

  const deleteUser = async (companyId: string, userId: string, reason?: string) => {
    const url = `/api/companies/${companyId}/users/${userId}`;
    try {
      await axios.delete(url, {
        data: reason ? { reason } : undefined,
      });
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while deleting user");
    }
  };

  const createUser = async (companyId: string, request: UserCreationRequest) => {
    const url = `/api/companies/${companyId}/user-creations`;
    try {
      const { data } = await axios.post<UserCreationScheduledDto>(url, request);
      return data;
    } catch (e: unknown) {
      const errorType = axios.isAxiosError(e) ? e.response?.data?.errorType : undefined;
      if (
        axios.isAxiosError(e) &&
        (errorType === "USER_EMAIL_TAKEN" || errorType === "USER_CREATION_PENDING")
      ) {
        throw new UserCreationConflictError(
          e.response?.data?.message,
          errorType,
          e.response?.data?.userId,
        );
      }
      throw toApiError(e, "The account could not be added. Try again.");
    }
  };

  const getUserCreation = async (companyId: string, userId: string) => {
    const url = `/api/companies/${companyId}/user-creations/${userId}`;
    try {
      const { data } = await axios.get<UserCreationDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "The scheduled account could not be loaded.");
    }
  };

  const getUserCreationContext = async (companyId: string) => {
    const url = `/api/companies/${companyId}/user-creations/context`;
    try {
      const { data } = await axios.get<UserCreationContextDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "The company's defaults for new accounts could not be loaded.");
    }
  };

  const changeUserEmail = async (companyId: string, userId: string, newEmail: string) => {
    const url = `/api/companies/${companyId}/users/${userId}/email`;
    try {
      await axios.patch(url, { newEmail });
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while changing user email");
    }
  };

  const getTemporaryRoles = async (companyId: string, userId: string) => {
    const url = `/api/maintenance/company/${companyId}/users/${userId}/temporary-roles`;
    const { data } = await axios.get(url);
    return data;
  };

  const grantTemporaryRole = async (
    companyId: string,
    userId: string,
    request: { application: string; roleName: string; durationMinutes: number },
  ) => {
    const url = `/api/maintenance/company/${companyId}/users/${userId}/temporary-roles`;
    const { data } = await axios.post(url, request);
    return data;
  };

  const revokeTemporaryRole = async (companyId: string, userId: string, grantId: number) => {
    const url = `/api/maintenance/company/${companyId}/users/${userId}/temporary-roles/${grantId}`;
    await axios.delete(url);
  };

  const bulkDeleteUsers = async (
    companyId: string,
    userIds: string[],
    reason?: string,
  ): Promise<{ scheduledCount: number }> => {
    const url = `/api/companies/${companyId}/users/bulk`;
    try {
      const { data } = await axios.delete<{ scheduledCount: number }>(url, {
        data: { userIds, reason },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while deleting users");
    }
  };

  const exportUsers = async (companyId: string, format: ExportFormat = "TSV"): Promise<Blob> => {
    const url = `/api/companies/${companyId}/users/export`;
    try {
      const { data } = await axios.get(url, { responseType: "blob", params: { format } });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while exporting users");
    }
  };

  const getRoles = async (companyId: string) => {
    const url = `/api/companies/${companyId}/roles`;
    try {
      const { data } = await axios.get<GetCompanyRolesResponse>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching Roles list");
    }
  };

  const exportRoles = async (companyId: string, format: ExportFormat = "TSV"): Promise<Blob> => {
    const url = `/api/companies/${companyId}/roles/export`;
    try {
      const { data } = await axios.get(url, { responseType: "blob", params: { format } });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while exporting roles");
    }
  };

  const getRoleDefinition = async (roleId: string, companyId: string) => {
    const url = `/api/companies/${companyId}/roles/${roleId}`;
    try {
      const { data } = await axios.get<GetRoleDefinitionResponse>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching Role");
    }
  };

  const createRole = async (payload: CreateRoleDefinitionPayload, companyId: string) => {
    const url = `/api/companies/${companyId}/roles`;
    try {
      const { data } = await axios.post<CreateRoleDefinitionResponse>(url, payload);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while creating role.");
    }
  };

  const updateRole = async (
    payload: UpdateRoleDefinitionPayload,
    roleId: string,
    companyId: string,
  ) => {
    const url = `/api/companies/${companyId}/roles/${roleId}`;
    try {
      const { data } = await axios.put<UpdateRoleDefinitionResponse>(url, payload);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while updating role");
    }
  };

  const getCompanyPermissions = async (companyId: string) => {
    const url = `/api/companies/${companyId}/permissions`;
    try {
      const { data } = await axios.get<GetPermissionsResponse>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching permissions");
    }
  };

  const getSkillPanelDashboards = async (companyId: string) => {
    const url = `/api/companies/${companyId}/skillpanel-dashboards`;
    try {
      const { data } = await axios.get<SkillPanelDashboardDto[]>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 502) {
        throw apiErrorWithMessage(e, "SkillPanel is unavailable.");
      }
      throw apiErrorWithMessage(e, "Error while fetching SkillPanel dashboards");
    }
  };

  const getRoleTemplates = async () => {
    const url = "/api/role-templates";
    try {
      const { data } = await axios.get<RoleTemplateDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching role templates");
    }
  };

  const deleteRole = async (roleId: string, companyId: string) => {
    const url = `/api/companies/${companyId}/roles/${roleId}`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.data?.message) {
        throw apiErrorWithMessage(e, e.response.data.message);
      }
      throw apiErrorWithMessage(e, `Error while deleting role`);
    }
  };

  const getUserImports = async (
    companyId: string,
    queryParams: { page?: number; size?: number },
  ) => {
    const url = `/api/companies/${companyId}/user-imports`;
    try {
      const { data } = await axios.get<PageResponse<UserImportListDto>>(url, {
        params: queryParams,
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching user imports");
    }
  };

  const getTeamImports = async (
    companyId: string,
    queryParams: { page?: number; size?: number },
  ) => {
    const url = `/api/companies/${companyId}/team-imports`;
    try {
      const { data } = await axios.get<PageResponse<TeamImportListDto>>(url, {
        params: queryParams,
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching team imports");
    }
  };

  const previewStructureImport = async (companyId: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post<StructurePlanDto>(
        `/api/companies/${companyId}/structure-imports/preview`,
        form,
      );
      return data;
    } catch (e: unknown) {
      throw toStructureImportError(e, "Error while previewing the structure file");
    }
  };

  const applyStructureImport = async (
    companyId: string,
    file: File,
    digest: string,
    deleteEmptyTeams: boolean,
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("digest", digest);
    form.append("deleteEmptyTeams", String(deleteEmptyTeams));
    try {
      const { data } = await axios.post<StructureApplyResultDto>(
        `/api/companies/${companyId}/structure-imports/apply`,
        form,
      );
      return data;
    } catch (e: unknown) {
      throw toStructureImportError(e, "Error while applying the structure file");
    }
  };

  const getStructureBatchStatus = async (companyId: string, batchId: string) => {
    try {
      const { data } = await axios.get<StructureBatchStatusDto>(
        `/api/companies/${companyId}/structure-imports/batches/${batchId}`,
      );
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching the structure import status");
    }
  };

  const getStructureImportMode = async (companyId: string) => {
    try {
      const { data } = await axios.get<StructureImportModeDto>(
        `/api/companies/${companyId}/structure-imports/mode`,
      );
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching where business units come from");
    }
  };

  /**
   * A 502 means SkillPanel failed the request (unreachable, or it refused it): its catalogue decides
   * every match, so nothing can be shown or applied.
   */
  const toJobTitleError = (e: unknown, fallback: string): ApiError => {
    if (axios.isAxiosError(e) && e.response?.status === 502) {
      return apiErrorWithMessage(
        e,
        "SkillPanel could not be reached or refused the request. Try again in a moment; if it keeps failing, SkillPanel's logs say why.",
      );
    }
    if (axios.isAxiosError(e) && e.response?.status === 409) {
      return apiErrorWithMessage(
        e,
        "This company's users are written by a dedicated importer, which cannot hold invitations back. Nothing was changed.",
      );
    }
    return apiErrorWithMessage(e, fallback);
  };

  const getJobTitles = async (companyId: string) => {
    try {
      const { data } = await axios.get<JobTitleReconciliationDto>(
        `/api/companies/${companyId}/job-titles`,
      );
      return data;
    } catch (e: unknown) {
      throw toJobTitleError(e, "Error while fetching job titles");
    }
  };

  const applyJobTitles = async (companyId: string, request: JobTitleApplyRequestDto) => {
    try {
      const { data } = await axios.post<JobTitleApplyResultDto>(
        `/api/companies/${companyId}/job-titles/apply`,
        request,
      );
      return data;
    } catch (e: unknown) {
      throw toJobTitleError(e, "Error while creating job positions");
    }
  };

  /** A refusal (409) explains itself, so its own message is kept; a 404 means the account is not of the company. */
  const resyncJobTitleAccount = async (companyId: string, userId: string) => {
    try {
      const { data } = await axios.post<JobTitleResyncResultDto>(
        `/api/companies/${companyId}/job-titles/accounts/${userId}/resync`,
      );
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 409) {
        throw toApiError(e, "The job title could not be re-synced. Nothing was changed.");
      }
      throw toJobTitleError(e, "Error while re-syncing the job title");
    }
  };

  const structureImportTemplateUrl = (companyId: string) =>
    `/api/companies/${companyId}/structure-imports/template`;
  const structureImportExportUrl = (companyId: string) =>
    `/api/companies/${companyId}/structure-imports/export`;

  const getSecurityLogs = async (
    companyId: string,
    queryParams?: {
      page?: number;
      size?: number;
      view?: SecurityLogView;
      operations?: SecurityOperation[];
      statuses?: SecurityLogStatus[];
      from?: string;
      userId?: string;
    },
  ) => {
    const url = `/api/companies/${companyId}/security-logs`;
    try {
      const { data } = await axios.get<PageResponse<SecurityLogDto>>(url, {
        params: queryParams,
        paramsSerializer: {
          indexes: null,
        },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching Security Logs");
    }
  };

  const getUserSecurityLogs = async (
    companyId: string,
    userId: string,
    params?: { size?: number; before?: string },
  ) => {
    const url = `/api/companies/${companyId}/users/${userId}/security-logs`;
    try {
      const { data } = await axios.get<CursorPageResponse<SecurityLogDto>>(url, {
        params,
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching user security logs");
    }
  };

  const getUserChanges = async (
    companyId: string,
    userId: string,
    params?: { size?: number; before?: string; beforeId?: number },
  ) => {
    const url = `/api/companies/${companyId}/users/${userId}/changes`;
    try {
      const { data } = await axios.get<CursorPageResponse<UserChangeDto>>(url, {
        params,
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching user changes");
    }
  };

  const getUserMapSync = async (companyId: string, userId: string) => {
    const url = `/api/companies/${companyId}/users/${userId}/map-sync`;
    try {
      const { data } = await axios.get<UserMapSyncDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw apiErrorWithMessage(
            e,
            "You don't have permission to view the SkillPanel sync check.",
          );
        }
        if (e.response?.status === 502) {
          throw apiErrorWithMessage(e, "SkillPanel unavailable.");
        }
      }
      throw apiErrorWithMessage(e, "Error while checking SkillPanel sync");
    }
  };

  const getSkillPanelVisibility = async (companyId: string, userId: string) => {
    const url = `/api/companies/${companyId}/users/${userId}/skillpanel-visibility`;
    try {
      const { data } = await axios.get<SkillPanelVisibilityDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching SkillPanel visibility");
    }
  };

  const getSkillPanelCustomFields = async (companyId: string, userId: string) => {
    const url = `/api/companies/${companyId}/users/${userId}/skillpanel-custom-fields`;
    try {
      const { data } = await axios.get<SkillPanelCustomFieldsDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching SkillPanel custom fields");
    }
  };

  const getDefaultRoles = async () => {
    const url = "/api/maintenance/default-roles";
    try {
      const { data } = await axios.get<{ id: string; name: string }[]>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching default roles");
    }
  };

  const getDynamicRoleAssignments = async (companyId: string) => {
    const url = `/api/companies/${companyId}/dynamic-role-assignments`;
    try {
      const { data } = await axios.get<DynamicRoleAssignmentDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching dynamic role assignments");
    }
  };

  const getMatchedUsersByRole = async (companyId: string) => {
    const url = `/api/companies/${companyId}/dynamic-role-assignments/matched-users`;
    try {
      const { data } = await axios.get<RoleMatchedUsersDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while counting users matched by dynamic rules");
    }
  };

  const createDynamicRoleAssignment = async (
    companyId: string,
    dto: DynamicRoleAssignmentRequestDto,
  ) => {
    const url = `/api/companies/${companyId}/dynamic-role-assignments`;
    try {
      const { data } = await axios.post<DynamicRoleAssignmentDto>(url, dto);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while creating the dynamic role assignment");
    }
  };

  const updateDynamicRoleAssignment = async (
    companyId: string,
    id: number,
    dto: DynamicRoleAssignmentRequestDto,
  ) => {
    const url = `/api/companies/${companyId}/dynamic-role-assignments/${id}`;
    try {
      const { data } = await axios.put<DynamicRoleAssignmentDto>(url, dto);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while updating the dynamic role assignment");
    }
  };

  const deleteDynamicRoleAssignment = async (companyId: string, id: number) => {
    const url = `/api/companies/${companyId}/dynamic-role-assignments/${id}`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      throw toApiError(e, "Error while deleting the dynamic role assignment");
    }
  };

  const postDynamicRoleAssignmentPreview = async (
    companyId: string,
    dto: DynamicRoleAssignmentRequestDto,
  ) => {
    const url = `/api/companies/${companyId}/dynamic-role-assignments/preview`;
    try {
      const { data } = await axios.post<DynamicRoleAssignmentPreviewDto>(url, dto);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while previewing the dynamic role assignment");
    }
  };

  const getDynamicRoleAssignmentPreview = async (companyId: string, id: number) => {
    const url = `/api/companies/${companyId}/dynamic-role-assignments/${id}/preview`;
    try {
      const { data } = await axios.get<DynamicRoleAssignmentPreviewDto>(url);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while previewing the dynamic role assignment");
    }
  };

  const getAdGroups = async (companyId: string) => {
    const url = `/api/companies/${companyId}/ad-groups`;
    try {
      const { data } = await axios.get<AdGroupDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while fetching AD groups");
    }
  };

  const getAdGroupProductMappings = async (companyId: string) => {
    const url = `/api/companies/${companyId}/ad-group-product-mappings`;
    try {
      const { data } = await axios.get<AdGroupProductMappingDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while fetching AD group product mappings");
    }
  };

  const createAdGroupProductMapping = async (
    companyId: string,
    dto: CreateAdGroupProductMappingDto,
  ) => {
    const url = `/api/companies/${companyId}/ad-group-product-mappings`;
    try {
      const { data } = await axios.post<AdGroupProductMappingDto>(url, dto);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while creating the AD group product mapping");
    }
  };

  const updateAdGroupProductMapping = async (
    companyId: string,
    id: number,
    dto: UpdateAdGroupProductMappingDto,
  ) => {
    const url = `/api/companies/${companyId}/ad-group-product-mappings/${id}`;
    try {
      const { data } = await axios.put<AdGroupProductMappingDto>(url, dto);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while updating the AD group product mapping");
    }
  };

  const deleteAdGroupProductMapping = async (companyId: string, id: number) => {
    const url = `/api/companies/${companyId}/ad-group-product-mappings/${id}`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      throw toApiError(e, "Error while deleting the AD group product mapping");
    }
  };

  const getAdGroupRoleMappings = async (companyId: string) => {
    const url = `/api/companies/${companyId}/ad-group-role-mappings`;
    try {
      const { data } = await axios.get<AdGroupRoleMappingDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while fetching AD group role mappings");
    }
  };

  const createAdGroupRoleMapping = async (companyId: string, dto: CreateAdGroupRoleMappingDto) => {
    const url = `/api/companies/${companyId}/ad-group-role-mappings`;
    try {
      const { data } = await axios.post<AdGroupRoleMappingDto>(url, dto);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while creating the AD group role mapping");
    }
  };

  const updateAdGroupRoleMapping = async (
    companyId: string,
    id: number,
    dto: UpdateAdGroupRoleMappingDto,
  ) => {
    const url = `/api/companies/${companyId}/ad-group-role-mappings/${id}`;
    try {
      const { data } = await axios.put<AdGroupRoleMappingDto>(url, dto);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while updating the AD group role mapping");
    }
  };

  const deleteAdGroupRoleMapping = async (companyId: string, id: number) => {
    const url = `/api/companies/${companyId}/ad-group-role-mappings/${id}`;
    try {
      await axios.delete(url);
    } catch (e: unknown) {
      throw toApiError(e, "Error while deleting the AD group role mapping");
    }
  };

  const getAdGroupSyncPreview = async (companyId: string) => {
    const url = `/api/companies/${companyId}/ad-group-sync/preview`;
    try {
      const { data } = await axios.get<AdGroupSyncPreviewDto>(url);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while computing the AD group impact preview");
    }
  };

  const postAdGroupSyncPreview = async (
    companyId: string,
    request: AdGroupSyncPreviewRequestDto,
  ) => {
    const url = `/api/companies/${companyId}/ad-group-sync/preview`;
    try {
      const { data } = await axios.post<AdGroupSyncPreviewDto>(url, request);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while computing the AD group impact preview");
    }
  };

  const triggerAdGroupSync = async (companyId: string) => {
    const url = `/api/companies/${companyId}/ad-group-sync`;
    try {
      const { data } = await axios.post<AdGroupSyncResultDto>(url);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while triggering the AD group re-sync");
    }
  };

  const getAdGroupSyncStatus = async (companyId: string) => {
    const url = `/api/companies/${companyId}/ad-group-sync/status`;
    try {
      const { data } = await axios.get<AdGroupSyncStatusDto>(url);
      return data;
    } catch (e: unknown) {
      throw toApiError(e, "Error while reading the AD group re-sync status");
    }
  };

  const logApplicationAccess = async (application: string) => {
    const url = `/api/audit/application-access?application=${encodeURIComponent(application)}`;
    try {
      await axios.post(url);
    } catch (e: unknown) {
      // Silently ignore - audit logging should not break the app
    }
  };

  const getDashboard = async (companyId: string) => {
    const url = `/api/companies/${companyId}/dashboard`;
    try {
      const { data } = await axios.get<DashboardDto>(url);
      return data;
    } catch (e: unknown) {
      // Keeps the status, so the dashboard can tell a lost permission from an outage
      throw toApiError(e, `Failed to fetch dashboard for company ${companyId}`);
    }
  };

  const getTeamTree = async (companyId: string) => {
    const url = `/api/companies/${companyId}/team-tree`;
    try {
      const { data } = await axios.get<TeamTreeBrowserNodeDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching team tree");
    }
  };

  const getTeamTreeDetails = async (companyId: string, teamId: string) => {
    const url = `/api/companies/${companyId}/team-tree/${teamId}`;
    try {
      const { data } = await axios.get<TeamTreeBrowserDetailsDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching team details");
    }
  };

  const getTeamTreeMembers = async (
    companyId: string,
    teamId: string,
    queryParams?: { search?: string; page?: number; size?: number },
  ) => {
    const url = `/api/companies/${companyId}/team-tree/${teamId}/members`;
    try {
      const { data } = await axios.get<Page<TeamMemberListDto>>(url, {
        params: queryParams,
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching team members");
    }
  };

  const getAccessRequests = async (companyId: string, statuses?: AccessRequestStatus[]) => {
    const url = `/api/companies/${companyId}/access-requests`;
    try {
      const { data } = await axios.get<AccessRequestDto[]>(url, {
        params: statuses ? { statuses } : undefined,
        paramsSerializer: {
          indexes: null,
        },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching access requests");
    }
  };

  const getMyAccessRequests = async (statuses?: AccessRequestStatus[]) => {
    const url = `/api/access-requests`;
    try {
      const { data } = await axios.get<AccessRequestDto[]>(url, {
        params: statuses ? { statuses } : undefined,
        paramsSerializer: {
          indexes: null,
        },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching own access requests");
    }
  };

  // Resolves to null when the ticket does not exist; throws when JIRA could not be asked.
  const getJiraIssue = async (key: string) => {
    const url = `/api/access-requests/jira-issues/${key}`;
    try {
      const { data } = await axios.get<JiraIssueDto>(url);
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        return null;
      }
      throw apiErrorWithMessage(e, `Error while fetching JIRA ticket ${key}`);
    }
  };

  const createAccessRequest = async (
    targetUserId: string,
    justification?: string,
    approverId?: string,
    jiraIssueKey?: string,
  ) => {
    const url = `/api/access-requests`;
    try {
      const command: CreateAccessRequestCommand = {
        targetUserId,
        justification,
        approverId,
        jiraIssueKey,
      };
      const { data } = await axios.post<AccessRequestDto>(url, command, {
        headers: { "Content-Type": "application/json" },
      });
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while creating access request");
    }
  };

  const activateAccessRequest = async (id: string) => {
    const url = `/api/access-requests/${id}/activate`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while activating access request");
    }
  };

  const resumeAccessRequest = async (id: string) => {
    const url = `/api/access-requests/${id}/resume`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while resuming access request session");
    }
  };

  const revokeAccessRequest = async (id: string) => {
    const url = `/api/access-requests/${id}/revoke`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while revoking access request");
    }
  };

  const cancelAccessRequest = async (id: string) => {
    const url = `/api/access-requests/${id}/cancel`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while cancelling access request");
    }
  };

  const completeAccessRequest = async (id: string) => {
    const url = `/api/access-requests/${id}/complete`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while ending access request session");
    }
  };

  const getAccessRequestSecurityLogs = async (id: string) => {
    const url = `/api/access-requests/${id}/security-logs`;
    try {
      const { data } = await axios.get<SecurityLogDto[]>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while fetching access request security logs");
    }
  };

  const validateAccessRequestToken = async (token: string) => {
    const url = `/api/access-requests/validate`;
    try {
      const { data } = await axios.get<AccessRequestValidationResult>(url, { params: { token } });
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        throw apiErrorWithMessage(e, "UNAUTHORIZED");
      }
      throw apiErrorWithMessage(e, "Error while validating access request token");
    }
  };

  const approveAccessRequestByToken = async (token: string) => {
    const url = `/api/access-requests/approve-by-token`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url, null, { params: { token } });
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        throw apiErrorWithMessage(e, "UNAUTHORIZED");
      }
      throw apiErrorWithMessage(e, "Error while approving access request");
    }
  };

  const denyAccessRequestByToken = async (token: string) => {
    const url = `/api/access-requests/deny-by-token`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url, null, { params: { token } });
      return data;
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 401) {
        throw apiErrorWithMessage(e, "UNAUTHORIZED");
      }
      throw apiErrorWithMessage(e, "Error while denying access request");
    }
  };

  const approveAccessRequestById = async (id: string) => {
    const url = `/api/access-requests/${id}/approve`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while approving access request");
    }
  };

  const denyAccessRequestById = async (id: string) => {
    const url = `/api/access-requests/${id}/deny`;
    try {
      const { data } = await axios.post<AccessRequestDto>(url);
      return data;
    } catch (e: unknown) {
      throw apiErrorWithMessage(e, "Error while denying access request");
    }
  };

  const enterSupportPersonaSession = async (companyId: string) => {
    const url = `/api/companies/${companyId}/support-persona/session`;
    try {
      const { status, data } = await axios.post<SupportPersonaSessionDto>(url);
      return { httpStatus: status, data };
    } catch (e: unknown) {
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        throw apiErrorWithMessage(e, "You can't open SkillPanel in this company as support.");
      }
      throw apiErrorWithMessage(e, "SkillPanel is unavailable. Try again in a minute.");
    }
  };

  return {
    getSelf,
    getCompany,
    getClientCompanies,
    searchCompanies,
    getApprovers,
    validateTargetUser,
    getAuthTokensSettings,
    updateAuthTokensSettings,
    getTeamAssignmentSettings,
    updateTeamAssignmentSettings,
    getCompanyInfo,
    getDefaultProducts,
    updateDefaultProducts,
    getDefaultRolesSettings,
    updateDefaultRolesSettings,
    getSamlConfiguration,
    setDefaultSamlConfiguration,
    updateSamlConfiguration,
    updateCompanyDomains,
    clearDefaultSamlConfiguration,
    getEntraIdConfiguration,
    updateEntraIdConfiguration,
    getMicrosoftIntegrationStatus,
    disconnectMicrosoftIntegration,
    disconnectMicrosoftBot,
    connectMicrosoftCompanyApp,
    getScimConfiguration,
    generateScimToken,
    revokeScimToken,
    updateScimConfiguration,
    listCompanyApiKeys,
    createCompanyApiKey,
    revokeCompanyApiKey,
    getServiceAccountStatus,
    createServiceAccount,
    getCompanyPlugins,
    enableCompanyPlugin,
    disableCompanyPlugin,
    getTeams,
    updateTeam,
    moveTeam,
    user: {
      getUsers,
      getUserCounts,
      getUserDetails,
      createUser,
      getUserCreation,
      getUserCreationContext,
      getSignInDiagnosis,
      getUserProducts,
      updateUserProducts,
      updateUserRoles,
      updateUserAuthMethod,
      updateUserEnabled,
      updateUserHiddenInSkillPanel,
      updateUserCustomFields,
      unlockUserAccount,
      adminResetPassword,
      deleteUser,
      changeUserEmail,
      bulkDeleteUsers,
      exportUsers,
      getTemporaryRoles,
      grantTemporaryRole,
      revokeTemporaryRole,
    },
    roles: {
      getRoles,
      exportRoles,
      getRoleDefinition,
      createRole,
      updateRole,
      deleteRole,
      getCompanyPermissions,
      getSkillPanelDashboards,
      getRoleTemplates,
    },
    userImports: {
      getUserImports,
    },
    teamImports: {
      getTeamImports,
    },
    structureImports: {
      previewStructureImport,
      applyStructureImport,
      getStructureBatchStatus,
      getStructureImportMode,
      structureImportTemplateUrl,
      structureImportExportUrl,
    },
    jobTitles: {
      getJobTitles,
      applyJobTitles,
      resyncAccount: resyncJobTitleAccount,
    },
    securityLogs: {
      getSecurityLogs,
      getUserSecurityLogs,
    },
    userChanges: {
      getUserChanges,
    },
    mapSync: {
      getUserMapSync,
      getSkillPanelVisibility,
      getSkillPanelCustomFields,
    },
    admin: {
      getDefaultRoles,
    },
    supportPersona: {
      enterSession: enterSupportPersonaSession,
    },
    dynamicRoles: {
      getDynamicRoleAssignments,
      getMatchedUsersByRole,
      createDynamicRoleAssignment,
      updateDynamicRoleAssignment,
      deleteDynamicRoleAssignment,
      postDynamicRoleAssignmentPreview,
      getDynamicRoleAssignmentPreview,
    },
    adGroups: {
      getAdGroups,
      getAdGroupProductMappings,
      createAdGroupProductMapping,
      updateAdGroupProductMapping,
      deleteAdGroupProductMapping,
      getAdGroupRoleMappings,
      createAdGroupRoleMapping,
      updateAdGroupRoleMapping,
      deleteAdGroupRoleMapping,
      getAdGroupSyncPreview,
      postAdGroupSyncPreview,
      triggerAdGroupSync,
      getAdGroupSyncStatus,
    },
    audit: {
      logApplicationAccess,
    },
    teamTree: {
      getTeamTree,
      getTeamTreeDetails,
      getTeamTreeMembers,
    },
    dashboard: {
      getDashboard,
    },
    accessRequests: {
      getAccessRequests,
      getMyAccessRequests,
      getJiraIssue,
      createAccessRequest,
      activateAccessRequest,
      resumeAccessRequest,
      revokeAccessRequest,
      cancelAccessRequest,
      completeAccessRequest,
      getAccessRequestSecurityLogs,
      validateAccessRequestToken,
      approveAccessRequestByToken,
      denyAccessRequestByToken,
      approveAccessRequestById,
      denyAccessRequestById,
    },
  };
};

interface RestApiContextProviderProps {
  children: React.ReactNode;
}
// The functions hold no state, so one instance serves every render.
const restApi = createApiProvider();

export const RestApiContextProvider = (props: RestApiContextProviderProps) => {
  const { children } = props;

  return <RestApiContext.Provider value={restApi}>{children}</RestApiContext.Provider>;
};
